/**
 * Données des guides pédagogiques MPDS — 24 protocoles.
 * Chaque entrée respecte la même structure que les guides détaillés
 * (douleur_thoracique, morsures) dans TeachingGuide.js.
 * `compactCommon: true` indique que la phase 1 utilise le rappel générique.
 *
 * Imports d'icônes : Heart, AlertTriangle, ShieldAlert, Clock, Users
 *   sont fournis par le composant parent via le paramètre `icons`.
 */
export const buildTeachingGuides = ({ Heart, AlertTriangle, ShieldAlert, Clock, Users }) => ({

  // ==========================================================
  // LOT 1 — Cardio-respiratoire
  // ==========================================================
  inconscience: {
    title: 'Inconscience / Évanouissement',
    subtitle: 'Guide pédagogique pour l\'enseignement du déterminant MPDS',
    compactCommon: true,
    intro: `L\'inconscience est l\'un des appels où la marge d\'erreur est la plus faible. Une victime "endormie" sur le sol peut être en arrêt cardiaque, en surdose, en coma diabétique ou simplement post-syncopale. La distinction se fait au téléphone en moins de 60 secondes, principalement à partir de deux questions : conscient ? respire normalement ?`,
    stats: [
      { icon: Heart, text: 'L\'arrêt cardiaque débute par une perte de conscience dans 90 % des cas.' },
      { icon: Clock, text: 'Chaque minute sans RCR diminue la survie de 7 à 10 %.' },
      { icon: AlertTriangle, text: 'Près de 40 % des "inconscients" rapportés sont en réalité en ACR initial.' },
    ],
    objectives: [
      'Différencier inconscience avec respiration normale vs ACR.',
      'Reconnaître la respiration agonale (gasps) comme signe d\'ACR.',
      'Guider le témoin vers la position latérale de sécurité si la victime respire.',
      'Initier la RCR immédiatement si la victime ne respire pas normalement.',
      'Évaluer les causes réversibles (hypoglycémie, surdose, traumatisme).',
    ],
    phases: [
      { n: 2, title: 'Conscience',
        why: 'Première question discriminante. Une personne consciente qui parle n\'est pas en ACR.',
        how: ['« Est-il complètement immobile, ne réagit pas si vous lui parlez ou le touchez ? »', '« Pouvez-vous le secouer doucement à l\'épaule ? Réagit-il ? »'],
        pitfalls: ['Accepter "il dort" sans vérifier — c\'est différent', 'Ne pas tester la stimulation tactile'] },
      { n: 3, title: 'Respiration',
        why: 'C\'est LE critère qui décide entre RCR vs position latérale.',
        how: ['« Respire-t-il normalement ou fait-il des bruits étranges, des halètements ? »', '« Mettez votre joue près de sa bouche — sentez-vous le souffle ? »', 'Si respiration agonale (gasps espacés) → CONSIDÉRER ACR'],
        pitfalls: ['Confondre gasps avec respiration', 'Ne pas reformuler si le témoin hésite'] },
      { n: 4, title: 'Cause suspectée',
        why: 'Oriente les soins pré-arrivée (sucre, naloxone, position).',
        how: ['« A-t-il un problème médical connu (diabète, épilepsie, cardiaque) ? »', '« Prend-il des médicaments ou de la drogue ? »', '« Est-il tombé ? S\'est-il cogné la tête ? »'],
        pitfalls: ['Trop investiguer avant de sécuriser respiration', 'Oublier la suspicion de trauma cervical'] },
      { n: 5, title: 'Position actuelle',
        why: 'Influence les consignes pré-arrivée (PLS, plan dur).',
        how: ['« Est-il sur le dos, le côté, le ventre ? »', '« Est-il sur une surface dure (sol) ou molle (lit) ? »'],
        pitfalls: ['Demander de déplacer si suspicion trauma rachidien'] },
    ],
    citizen_behavior: {
      title: 'Communication avec un témoin face à une victime inconsciente',
      tone: ['Voix calme et structurée — le témoin est souvent terrifié', 'Donner les instructions une à la fois, en attendant chaque exécution', 'Reformuler les réponses pour rassurer'],
      phrases_utiles: ['« Restez avec moi, on va l\'aider ensemble. »', '« Mettez votre main sur sa poitrine — sentez-vous des mouvements ? »', '« Vous êtes ses yeux et ses oreilles pour moi. »'],
      phrases_a_eviter: ['« Ne paniquez pas »', '« C\'est probablement pas grave »', '« Attendez l\'ambulance sans rien faire »'],
      panique_gestion: ['Si le témoin pleure → respiration guidée 3 cycles puis reprise', 'Si plusieurs témoins → désigner UN seul interlocuteur'],
    },
    red_flags: ['Respiration agonale (gasps)', 'Absence totale de réaction', 'Coloration bleuâtre (cyanose)', 'Pupilles dilatées non réactives', 'Suspicion overdose (seringue, pilules)', 'Trauma crânien associé', 'Convulsions précédentes'],
    pre_arrival: [
      'Si respire normalement → Position latérale de sécurité (sauf trauma)',
      'Si ne respire pas normalement → RCR immédiate',
      'Surveiller la respiration continuellement',
      'Si suspicion overdose opiacés → naloxone (Narcan) si disponible',
      'Si suspicion hypoglycémie et conscience partielle → sucre sublingual',
      'Ne PAS donner à boire ou à manger',
      'Ne PAS laisser seul',
    ],
    common_errors: [
      'Accepter "il respire" sans vérifier la qualité de la respiration',
      'Hésiter à demander la RCR par peur de se tromper',
      'Sous-estimer une syncope récidivante',
      'Oublier de demander si trauma associé',
    ],
    discussion_questions: [
      'Un homme s\'effondre dans un restaurant. Le témoin dit "il dort, il respire". Vos prochaines questions ?',
      'Une jeune femme inconsciente après une soirée, respiration normale. PLS ou RCR ?',
      'Personne âgée trouvée au sol le matin par un voisin. Quels risques associés ?',
    ],
    references: [
      'Clawson J., MPDS Protocol 31 — Unconscious/Fainting, 13th Edition',
      'AHA Guidelines for CPR and ECC — 2020',
      'Heart & Stroke Foundation — Lignes directrices BLS 2024',
    ],
  },

  difficulte_respiratoire: {
    title: 'Problèmes Respiratoires',
    subtitle: 'Guide pédagogique pour l\'enseignement du déterminant MPDS',
    compactCommon: true,
    intro: `La détresse respiratoire est un signe d\'alarme universel : asthme, OAP, EP, anaphylaxie, pneumothorax, obstruction des voies aériennes. Le rôle du répartiteur est d\'évaluer la sévérité en quelques secondes (peut-il parler ? change-t-il de couleur ?) et de calmer le patient — l\'anxiété aggrave directement la dyspnée.`,
    stats: [
      { icon: Heart, text: 'L\'asthme touche 1 Canadien sur 12 ; 250 décès/an évitables.' },
      { icon: AlertTriangle, text: 'Une saturation < 90 % cause des lésions cérébrales en 4-6 min.' },
      { icon: Clock, text: 'L\'OAP cardiogénique se manifeste typiquement la nuit (orthopnée).' },
    ],
    objectives: [
      'Évaluer la sévérité d\'une dyspnée au téléphone (échelle parler/marcher).',
      'Identifier les causes urgentes (anaphylaxie, OAP, pneumothorax, asthme sévère).',
      'Guider l\'utilisation de pompes (ventoline, EpiPen) à distance.',
      'Positionner correctement le patient (assis penché en avant).',
      'Détecter les signes pré-arrêt respiratoire.',
    ],
    phases: [
      { n: 2, title: 'Sévérité de la dyspnée',
        why: 'Le degré d\'effort respiratoire dicte la priorité ambulancière.',
        how: ['« Peut-il parler en phrases complètes, par mots, ou pas du tout ? »', '« Sa respiration est-elle bruyante (sifflement, gargouillis) ? »', '« Sa coloration est-elle normale, pâle ou bleuâtre ? »'],
        pitfalls: ['Se fier uniquement à la fréquence respiratoire estimée', 'Ne pas demander la coloration des lèvres et ongles'] },
      { n: 3, title: 'Antécédents et déclencheurs',
        why: 'Asthme, BPCO, allergie, insuffisance cardiaque orientent les soins.',
        how: ['« A-t-il de l\'asthme, des allergies, des problèmes cardiaques ? »', '« A-t-il sa pompe (ventoline) avec lui ? »', '« A-t-il mangé quelque chose, été piqué par un insecte ? »'],
        pitfalls: ['Oublier de chercher l\'anaphylaxie en cours', 'Confondre asthme et OAP chez la personne âgée'] },
      { n: 4, title: 'Position et confort',
        why: 'La position assise penchée améliore les volumes pulmonaires.',
        how: ['« Est-il assis, couché, debout ? »', '« Est-il plus confortable assis ou couché ? »'],
        pitfalls: ['Faire allonger un patient en détresse — aggrave la dyspnée'] },
    ],
    citizen_behavior: {
      title: 'Communiquer avec un patient en détresse respiratoire',
      tone: ['Parler lentement — il a besoin de toute son énergie pour respirer', 'Phrases courtes pour qu\'il réponde par OUI/NON ou hochement', 'Voix très calme — l\'anxiété aggrave la dyspnée'],
      phrases_utiles: ['« Respirez lentement par le nez, soufflez par la bouche. »', '« Asseyez-vous bien droit, penchez-vous légèrement en avant. »', '« Je reste avec vous, l\'ambulance arrive. »'],
      phrases_a_eviter: ['« Calmez-vous » (impossible et culpabilisant)', '« Essayez de respirer normalement »', '« Ce n\'est probablement rien »'],
      panique_gestion: ['Demander au patient de souffler dans ses lèvres pincées (lip-pursing)', 'Si crise d\'angoisse suspectée → respiration carrée (4-4-4-4)', 'Si proche présent → demander une main rassurante sur l\'épaule'],
    },
    red_flags: ['Incapacité de parler', 'Coloration bleue/grise', 'Tirage musculaire visible', 'Sueurs froides', 'Confusion', 'Bave aux lèvres (œdème glotte)', 'Antécédents asthme sévère / intubation', 'Douleur thoracique associée'],
    pre_arrival: [
      'Position assise droite, légèrement penchée en avant',
      'Desserrer vêtements col, ceinture',
      'Ouvrir une fenêtre — air frais',
      'Pompe (ventoline) 2 inhalations si présente et habituelle',
      'EpiPen IMMÉDIATEMENT si anaphylaxie suspectée',
      'Ne PAS faire allonger',
      'Ne PAS faire boire',
      'Rester à proximité, parler doucement',
    ],
    common_errors: [
      'Faire allonger le patient',
      'Ne pas reconnaître une anaphylaxie débutante',
      'Sous-estimer une "petite" dyspnée chez asthmatique',
      'Oublier de demander coloration des lèvres',
    ],
    discussion_questions: [
      'Femme de 60 ans, dyspnée nocturne soudaine, ne peut s\'allonger. OAP ou EP ?',
      'Enfant 8 ans, sifflement audible, ne parle plus que par mots. Conduite ?',
      'Homme avec ronchi laryngés et lèvres gonflées après repas. Que faire ?',
    ],
    references: [
      'Clawson J., MPDS Protocol 6 — Breathing Problems',
      'GINA — Global Initiative for Asthma 2024',
      'Société canadienne de thoracologie — Lignes directrices BPCO',
    ],
  },

  hemorragie_majeure: {
    title: 'Hémorragie / Saignement Majeur',
    subtitle: 'Guide pédagogique pour l\'enseignement du déterminant MPDS',
    compactCommon: true,
    intro: `Une hémorragie non contrôlée peut tuer en 3 à 5 minutes. La compression directe est la mesure salvatrice. Le rôle du répartiteur est d\'évaluer le type de saignement (artériel pulsatile, veineux, capillaire), d\'estimer le volume perdu, et de guider les premiers gestes — sans attendre l\'ambulance.`,
    stats: [
      { icon: Heart, text: 'L\'hémorragie est la 2e cause de décès évitable en traumatisme.' },
      { icon: Clock, text: 'Un adulte perdant 2 L de sang entre en choc hémorragique.' },
      { icon: AlertTriangle, text: '90 % des décès par hémorragie surviennent avant l\'hôpital — d\'où l\'importance de la compression.' },
    ],
    objectives: [
      'Différencier hémorragie artérielle (rouge vif, jet pulsatile) vs veineuse (sombre, continue).',
      'Guider l\'application d\'une compression directe efficace.',
      'Identifier les sites où le garrot est justifié (membre, hémorragie massive incontrôlable).',
      'Reconnaître les signes précoces de choc hémorragique.',
      'Conserver les tissus avulsés correctement (réimplantation).',
    ],
    phases: [
      { n: 2, title: 'Caractéristiques du saignement',
        why: 'Détermine l\'urgence et la conduite (compression vs garrot).',
        how: ['« Le sang gicle-t-il en jet ou coule-t-il doucement ? »', '« Combien de sang environ — une tasse, un bol, plus ? »', '« Où exactement sur le corps ? »'],
        pitfalls: ['Sous-estimer un saignement sur vêtements foncés', 'Ne pas chercher saignements multiples'] },
      { n: 3, title: 'Cause',
        why: 'Plaie vs hémorragie spontanée (anticoagulants, varices œsophagiennes, etc.).',
        how: ['« Comment c\'est arrivé — coupure, accident, spontané ? »', '« Prend-il des anticoagulants (Coumadin, Eliquis, Xarelto) ? »', '« A-t-il une hémophilie ou trouble de coagulation ? »'],
        pitfalls: ['Oublier la question des anticoagulants — multiplie le risque'] },
      { n: 4, title: 'État du patient',
        why: 'Détection précoce du choc hémorragique (pâleur, confusion).',
        how: ['« Est-il pâle, en sueur, agité ? »', '« Est-il conscient et orienté ? »', '« A-t-il soif intense, vertiges ? »'],
        pitfalls: ['Confondre anxiété et choc débutant'] },
    ],
    citizen_behavior: {
      title: 'Guider un témoin face à du sang',
      tone: ['Voix très calme — la vue du sang panique', 'Ne pas décrire les détails — focaliser sur l\'action', 'Encourager après chaque geste réalisé'],
      phrases_utiles: ['« Pressez FORT directement sur la plaie avec un tissu propre. »', '« Ne lâchez pas, même si ça saigne encore — gardez la pression. »', '« Vous arrêtez l\'hémorragie en ce moment — c\'est exactement ce qu\'il faut. »'],
      phrases_a_eviter: ['« Regardez la plaie » (peut faire défaillir le témoin)', '« Mettez un garrot » (sauf si vraiment indiqué)', '« Lavez la plaie » (en pleine hémorragie)'],
      panique_gestion: ['Si le témoin a un malaise vagal → s\'asseoir, jambes surélevées', 'Si plusieurs personnes → désigner un compresseur et un communicateur', 'Si enfant blessé → éloigner les autres enfants'],
    },
    red_flags: ['Saignement en jet pulsatile (artériel)', 'Sang qui imbibe rapidement plusieurs compresses', 'Pâleur extrême + sueurs', 'Confusion ou perte de conscience', 'Tachycardie perçue (pouls rapide)', 'Anticoagulants', 'Amputation traumatique', 'Hémorragie au cou (risque obstruction trachée)'],
    pre_arrival: [
      'Compression directe FERME et CONTINUE sur la plaie',
      'Ne PAS retirer les compresses imbibées — superposer',
      'Si membre : élever au-dessus du niveau du cœur',
      'Allonger le patient, jambes surélevées si signes de choc',
      'Couvrir d\'une couverture (prévention hypothermie)',
      'Garrot SEULEMENT si hémorragie incontrôlable d\'un membre',
      'Conserver les tissus avulsés dans un sac propre humide, dans un autre sac avec glace',
      'Surveiller la conscience',
    ],
    common_errors: [
      'Compression trop molle ou intermittente',
      'Retirer les compresses pour "voir"',
      'Garrot trop précoce ou mal posé',
      'Faire boire le patient',
      'Oublier la prévention de l\'hypothermie',
    ],
    discussion_questions: [
      'Homme coupé à la cuisse avec scie circulaire, sang qui gicle. Garrot ou compression ?',
      'Femme âgée sous Coumadin, saignement de nez incontrôlable depuis 30 min. Que faire ?',
      'Travailleur avec doigt sectionné. Comment conserver le doigt pour réimplantation ?',
    ],
    references: [
      'Clawson J., MPDS Protocol 21 — Hemorrhage/Lacerations',
      'Stop the Bleed — American College of Surgeons',
      'TCCC — Tactical Combat Casualty Care (principes civils)',
    ],
  },

  convulsions: {
    title: 'Convulsions',
    subtitle: 'Guide pédagogique pour l\'enseignement du déterminant MPDS',
    compactCommon: true,
    intro: `Une crise convulsive est impressionnante pour les témoins mais rarement immédiatement mortelle. Le danger réel vient des chutes, traumatismes pendant la crise, et de l\'état post-critique (inconscience). Une crise > 5 minutes ou des crises répétées sans reprise de conscience = état de mal épileptique = urgence absolue.`,
    stats: [
      { icon: Heart, text: '1 Canadien sur 100 vit avec l\'épilepsie.' },
      { icon: AlertTriangle, text: 'L\'état de mal épileptique a une mortalité de 20 % sans traitement.' },
      { icon: Clock, text: 'Une crise > 5 min nécessite une intervention médicamenteuse rapide.' },
    ],
    objectives: [
      'Reconnaître les types de crises (tonico-cloniques, partielles, absences).',
      'Identifier l\'état de mal épileptique (crise > 5 min ou récurrente).',
      'Guider la protection du patient pendant la crise (sans contention).',
      'Évaluer la cause possible (fièvre chez enfant, sevrage, AVC).',
      'Guider la gestion de l\'état post-critique.',
    ],
    phases: [
      { n: 2, title: 'État actuel',
        why: 'Crise active vs phase post-critique change la conduite.',
        how: ['« Convulse-t-il encore en ce moment ? »', '« Si oui, depuis combien de temps ? »', '« Sa respiration est-elle audible ? »'],
        pitfalls: ['Ne pas chronométrer la crise', 'Confondre tremblements post-critiques et crise active'] },
      { n: 3, title: 'Antécédents',
        why: 'Première crise vs épilepsie connue, contexte.',
        how: ['« A-t-il déjà fait des crises avant ? »', '« Prend-il des médicaments antiépileptiques ? »', '« A-t-il de la fièvre ? S\'est-il cogné la tête ? »', '« Consomme-t-il alcool, drogue ? Sevrage récent ? »'],
        pitfalls: ['Ne pas chercher sevrage alcool', 'Oublier la fièvre chez l\'enfant'] },
      { n: 4, title: 'Sécurité immédiate',
        why: 'Prévenir les blessures pendant la crise.',
        how: ['« Y a-t-il des objets dangereux autour ? »', '« Est-il sur surface dure ou molle ? »', '« Peut-il être placé sur le côté en sécurité ? »'],
        pitfalls: ['Demander de tenir le patient (contre-indiqué)'] },
    ],
    citizen_behavior: {
      title: 'Guider un témoin face à une crise convulsive',
      tone: ['Ton calme — la crise est très impressionnante mais rarement mortelle', 'Donner des consignes claires et brèves', 'Préparer le témoin à la phase post-critique (confusion, fatigue)'],
      phrases_utiles: ['« Ne le retenez pas, ne mettez rien dans sa bouche. »', '« Éloignez les meubles ou objets dangereux. »', '« Mettez quelque chose de mou sous sa tête. »', '« Une fois la crise terminée, mettez-le sur le côté. »'],
      phrases_a_eviter: ['« Mettez un objet dans la bouche » (TRÈS DANGEREUX)', '« Retenez-le pour qu\'il arrête » (peut causer fractures)', '« Donnez-lui à boire dès qu\'il s\'arrête »'],
      panique_gestion: ['Si enfant en crise fébrile → rassurer parent, c\'est généralement bénin', 'Si témoin n\'a jamais vu de crise → décrire ce qui va arriver (cyanose, salive, etc.)'],
    },
    red_flags: ['Crise > 5 minutes (état de mal épileptique)', 'Crises répétées sans reprise de conscience', 'Première crise chez adulte', 'Crise post-traumatisme crânien', 'Cyanose persistante', 'Diabétique connu', 'Femme enceinte (éclampsie)', 'Sevrage alcoolique', 'Fièvre élevée (> 39 °C)'],
    pre_arrival: [
      'Éloigner les objets dangereux',
      'Placer quelque chose de mou sous la tête',
      'NE PAS retenir les mouvements',
      'NE RIEN mettre dans la bouche',
      'Desserrer les vêtements au cou',
      'Chronométrer la crise',
      'Après la crise → position latérale de sécurité',
      'Surveiller respiration en phase post-critique',
      'Ne pas réveiller brutalement, ne pas faire boire',
    ],
    common_errors: [
      'Mettre quelque chose dans la bouche (mythe dangereux)',
      'Tenter de contenir les mouvements',
      'Oublier de chronométrer',
      'Sous-estimer une crise > 5 min',
      'Donner à boire en phase post-critique',
    ],
    discussion_questions: [
      'Enfant de 18 mois en crise fébrile à 39,5 °C. Que dire aux parents paniqués ?',
      'Patient diabétique inconscient avec mouvements. Crise ou hypoglycémie ?',
      'Patient ayant fait 2 crises en 30 min sans reprise de conscience. Pronostic ?',
    ],
    references: [
      'Clawson J., MPDS Protocol 12 — Convulsions/Seizures',
      'Société canadienne de l\'épilepsie — Guide premiers secours',
      'AAN — Status Epilepticus Guidelines',
    ],
  },

  avc_suspecte: {
    title: 'AVC Suspecté (Accident Vasculaire Cérébral)',
    subtitle: 'Guide pédagogique pour l\'enseignement du déterminant MPDS',
    compactCommon: true,
    intro: `« Time is brain » — chaque minute d\'AVC ischémique détruit 1,9 million de neurones. La reconnaissance précoce et l\'orientation vers un centre tertiaire pour thrombolyse/thrombectomie (fenêtre 4,5 à 24 h selon le cas) sont les seuls moyens de réduire les séquelles. L\'échelle FAST (Face, Arms, Speech, Time) est l\'outil de référence.`,
    stats: [
      { icon: Heart, text: '~62 000 AVC par an au Canada — 1 toutes les 9 minutes.' },
      { icon: Clock, text: 'Pour chaque heure de retard, le cerveau vieillit de 3,6 ans (Saver, 2006).' },
      { icon: AlertTriangle, text: 'Seuls 10 % des patients arrivent à l\'hôpital dans la fenêtre de thrombolyse.' },
    ],
    objectives: [
      'Appliquer l\'échelle FAST par téléphone (Face-Arms-Speech-Time).',
      'Identifier précisément l\'heure du DERNIER moment où le patient était normal.',
      'Différencier AVC ischémique vs hémorragique cliniquement (impossible) — orienter pareil.',
      'Reconnaître l\'AIT (mini-AVC) comme urgence (récidive 20 % en 90 jours).',
      'Ne PAS administrer d\'aspirine (contraste douleur thoracique).',
    ],
    phases: [
      { n: 2, title: 'Test FAST',
        why: 'Outil validé internationalement — sensibilité 80 % pour AVC ischémique.',
        how: ['F — « Demandez-lui de sourire. Un côté du visage tombe-t-il ? »', 'A — « Demandez-lui de lever les deux bras. Un bras retombe-t-il ? »', 'S — « Demandez-lui de répéter une phrase simple. Mots brouillés ? »', 'T — « Quand l\'avez-vous vu en bonne santé pour la DERNIÈRE fois ? »'],
        pitfalls: ['Accepter "ce matin" — préciser l\'heure exacte', 'Confondre dysarthrie et aphasie'] },
      { n: 3, title: 'Symptômes additionnels',
        why: 'Élargit le tableau (territoires vertébro-basilaires souvent oubliés).',
        how: ['« Voit-il double, voit-il flou d\'un côté ? »', '« A-t-il des étourdissements, du mal à marcher ? »', '« A-t-il un mal de tête soudain et très intense ? »', '« A-t-il vomi sans nausée préalable ? »'],
        pitfalls: ['Ignorer le syndrome cérébelleux', 'Confondre céphalée en coup de tonnerre avec migraine'] },
      { n: 4, title: 'Antécédents et médicaments',
        why: 'Contre-indications à la thrombolyse (anticoagulants, chirurgie récente).',
        how: ['« Prend-il des anticoagulants ? »', '« A-t-il déjà fait un AVC ? Une intervention récente ? »', '« Diabétique, hypertension, fibrillation auriculaire ? »'],
        pitfalls: ['Oublier de demander la liste des médicaments'] },
    ],
    citizen_behavior: {
      title: 'Patient et famille face à un AVC',
      tone: ['Le patient peut comprendre mais ne pas répondre — parler comme s\'il était conscient', 'Famille très anxieuse — donner des consignes très claires', 'L\'heure de début est cruciale — insister'],
      phrases_utiles: ['« L\'heure exacte est extrêmement importante pour les médecins. »', '« Asseyez-le en position semi-assise. »', '« Notez les médicaments qu\'il prend pour donner aux ambulanciers. »'],
      phrases_a_eviter: ['« Donnez-lui de l\'aspirine » (peut aggraver un AVC hémorragique)', '« Faites-le marcher »', '« Donnez-lui à boire » (risque fausse route)'],
      panique_gestion: ['Demander à un proche de noter chronologiquement les symptômes', 'Si patient agité → ne pas contenir, parler doucement', 'Préparer la liste de médicaments à donner aux secours'],
    },
    red_flags: ['Asymétrie faciale soudaine', 'Hémiplégie ou hémiparésie soudaine', 'Aphasie soudaine', 'Céphalée soudaine "en coup de tonnerre"', 'Perte de vision unilatérale', 'Vertige + déséquilibre + diplopie', 'Vomissement sans nausée', 'Trouble de conscience progressif', 'Convulsions associées'],
    pre_arrival: [
      'Position semi-assise (tête légèrement surélevée 30°)',
      'NE PAS donner d\'aspirine, médicament, boisson ou nourriture',
      'Noter l\'heure exacte du DÉBUT des symptômes',
      'Préparer la liste de médicaments du patient',
      'Préparer la carte d\'assurance maladie',
      'Identifier la personne ayant assisté au début',
      'Surveiller respiration et conscience',
      'Ne PAS laisser conduire le patient ou un proche',
    ],
    common_errors: [
      'Donner de l\'aspirine "comme pour un infarctus"',
      'Accepter "ce matin" sans préciser l\'heure',
      'Faire marcher le patient',
      'Sous-estimer un AIT (symptômes transitoires)',
      'Oublier les troubles du tronc cérébral (vertige, diplopie)',
    ],
    discussion_questions: [
      'Patient avec hémiparésie droite et aphasie depuis 1 h. Pourquoi est-ce critique ?',
      'Femme âgée avec étourdissements et diplopie soudaine, ne tient pas debout. AVC ?',
      'Patient qui a eu une faiblesse du bras pendant 20 min puis tout est revenu. Que faire ?',
    ],
    references: [
      'Clawson J., MPDS Protocol 28 — Stroke/CVA/TIA',
      'Stroke Best Practice Recommendations — Canadian Stroke Network 2023',
      'Saver JL — Time is Brain — Quantified (Stroke 2006)',
    ],
  },

  reaction_allergique: {
    title: 'Réaction Allergique / Anaphylaxie',
    subtitle: 'Guide pédagogique pour l\'enseignement du déterminant MPDS',
    compactCommon: true,
    intro: `L\'anaphylaxie est une réaction allergique systémique potentiellement mortelle qui évolue en minutes. L\'épinéphrine (EpiPen) est le seul traitement qui sauve — chaque minute de retard augmente la mortalité. Le répartiteur doit identifier les signes d\'évolution vers anaphylaxie et ordonner l\'EpiPen sans hésitation.`,
    stats: [
      { icon: Heart, text: 'Mortalité de l\'anaphylaxie : 0,5-2 % — entièrement évitable avec EpiPen précoce.' },
      { icon: Clock, text: 'La fenêtre critique est de 5 à 15 minutes après exposition.' },
      { icon: AlertTriangle, text: '20 % des décès anaphylactiques surviennent malgré l\'EpiPen — souvent administré trop tard.' },
    ],
    objectives: [
      'Différencier urticaire isolée vs anaphylaxie systémique.',
      'Identifier les 2 critères diagnostiques (peau + 1 système).',
      'Ordonner l\'EpiPen IMMÉDIATEMENT sans attendre.',
      'Reconnaître la réaction biphasique (récidive à 1-8 h).',
      'Guider la position selon symptôme dominant (respiratoire vs choc).',
    ],
    phases: [
      { n: 2, title: 'Signes systémiques',
        why: 'L\'anaphylaxie nécessite peau + 1 autre système atteint.',
        how: ['« A-t-il des plaques rouges qui démangent (urticaire) ? »', '« Sa respiration est-elle sifflante, voix rauque ? »', '« Vomit-il, a-t-il des crampes abdominales ? »', '« Est-il pâle, étourdi, sur le point de s\'évanouir ? »'],
        pitfalls: ['Attendre tous les signes — UN seul autre système atteint suffit', 'Confondre prurit isolé et anaphylaxie'] },
      { n: 3, title: 'Déclencheur',
        why: 'Aide à anticiper récidive et orienter traitement.',
        how: ['« Qu\'a-t-il mangé, bu, ou pris comme médicament ? »', '« A-t-il été piqué par un insecte ? »', '« Combien de temps après l\'exposition ? »', '« A-t-il déjà eu cette réaction ? »'],
        pitfalls: ['Oublier les médicaments (antibiotiques, AINS)'] },
      { n: 4, title: 'EpiPen et traitement',
        why: 'L\'EpiPen est la PREMIÈRE LIGNE — pas les antihistaminiques.',
        how: ['« Avez-vous un EpiPen disponible ? »', '« Combien d\'EpiPen ? »', '« Si oui → ADMINISTREZ MAINTENANT, je vous guide »', '« A-t-il pris autre chose (Benadryl) ? »'],
        pitfalls: ['Hésiter à ordonner l\'EpiPen', 'Penser que Benadryl suffit'] },
    ],
    citizen_behavior: {
      title: 'Guider l\'utilisation de l\'EpiPen',
      tone: ['Urgence absolue mais voix calme — chaque seconde compte', 'Instructions très simples, 1 à la fois', 'Compter à voix haute pendant l\'injection'],
      phrases_utiles: ['« Retirez le capuchon bleu. Appuyez l\'autre extrémité ORANGE sur la cuisse, à travers les vêtements si nécessaire. »', '« Maintenez 3 secondes — je compte avec vous : 1, 2, 3. »', '« Massez la zone 10 secondes après. Notez l\'heure. »'],
      phrases_a_eviter: ['« Attendez de voir si ça empire » (TRÈS DANGEREUX)', '« Donnez du Benadryl » (ne remplace pas EpiPen)', '« Faites-le vomir »'],
      panique_gestion: ['Si patient panique → respiration guidée pendant 3 cycles', 'Si plusieurs personnes → désigner UNE personne pour l\'EpiPen', 'Demander immédiatement si 2e dose disponible (effet biphasique)'],
    },
    red_flags: ['Voix rauque, stridor', 'Difficulté à respirer ou à avaler', 'Œdème de la langue ou du visage', 'Hypotension (pâleur, malaise)', 'Tachycardie marquée', 'Vomissements + crampes', 'Perte de conscience', 'Exposition à un allergène connu', 'Antécédent d\'anaphylaxie'],
    pre_arrival: [
      'EpiPen IMMÉDIATEMENT (cuisse externe, à travers les vêtements)',
      'Allonger le patient JAMBES SURÉLEVÉES (sauf détresse respiratoire → assis)',
      'Si après 5-10 min sans amélioration → 2e EpiPen si disponible',
      'NE PAS faire boire ou manger',
      'NE PAS laisser se lever',
      'Préparer la 2e dose au cas où',
      'Conserver l\'auto-injecteur usagé pour les ambulanciers',
      'Surveiller respiration et conscience continuellement',
    ],
    common_errors: [
      'Attendre "pour voir" avant l\'EpiPen',
      'Donner Benadryl à la place de l\'EpiPen',
      'Mettre le patient debout',
      'Oublier la réaction biphasique',
      'Ne pas demander si 2e dose disponible',
    ],
    discussion_questions: [
      'Enfant pique par abeille avec urticaire généralisée + démangeaison de la gorge. Anaphylaxie ?',
      'Patient ayant pris son EpiPen, symptômes améliorés. Doit-il quand même aller à l\'hôpital ?',
      'Adulte avec œdème de la langue sans urticaire après IECA. Conduite ?',
    ],
    references: [
      'Clawson J., MPDS Protocol 2 — Allergies/Stings',
      'INSPQ — Lignes directrices anaphylaxie 2023',
      'WAO Anaphylaxis Guidelines 2020',
    ],
  },

});
